<!--
  movedLessonTeacherToDoDetails: to-do "Verschobene Stunde …: Lehrkraft klären" (source movedLessonTeacher).
  Shows what happened ("Stunde vom 14.10. auf 22.10. verschoben – über einen Lehrkraftwechsel …") and, while open, the
  decision right at the to-do: "Lehrkraft klären" opens MovedLessonTeacherDialog for the lesson move, "Im Kalender öffnen"
  the calendar at the new date. Deciding completes the to-do in the course-manager; the lists reload via ToDoBus.
  Rights: deciding needs course read + update of `teacher` (like the calendar panel); teacher names only with teacher read.
-->
<template>
  <div :class="{ 'mlt-todo--dense': dense }" class="mlt-todo">
    <div class="acq-muted mlt-todo__text">{{ text }}</div>
    <div v-if="!dense && !completed" class="mlt-todo__actions">
      <button v-if="canDecide" class="cta cta--primary" type="button" @click.stop="showDialog = true">
        <v-icon class="cta__icon" small>mdi-account-question</v-icon>Lehrkraft klären
      </button>
      <router-link v-if="calendarLink" :to="calendarLink" class="cta cta--ghost" @click.native.stop>
        <v-icon class="cta__icon" small>mdi-calendar-week</v-icon>Im Kalender öffnen
      </router-link>
      <span v-if="!canDecide" class="acq-tiny">Klären kann, wer die Lehrkraft des Kurses ändern darf.</span>
    </div>
    <moved-lesson-teacher-dialog v-if="showDialog && move" v-model="showDialog" :move="move" @decided="onDecided"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import {RawLocation} from 'vue-router/types/router';
import MovedLessonTeacherDialog from '@/components/timetable2/dialogs/MovedLessonTeacherDialog.vue';
import {
  movedLessonCalendarLink,
  MovedLessonTeacherMove,
  movedLessonTeacherMove,
  movedLessonTeacherSourceOf,
  movedLessonTeacherText,
} from '@/components/toDos/movedLessonTeacherToDo.helper';
import {MovedLessonTeacherToDoSource} from '@/documentTypes/to-do-manager/ToDoItemSource';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {ToDoBus} from '@/busses/ToDoBus';

export default Vue.extend({
  components: {MovedLessonTeacherDialog},
  props: {
    /** The to-do (source.type movedLessonTeacher) */
    item: {type: Object as () => { source?: unknown; completed?: boolean }, required: true},
    /** Only the explanation (compact lists such as the dashboard) */
    dense: {type: Boolean, default: false},
  },
  data: () => ({
    showDialog: false,
  }),
  computed: {
    source(): MovedLessonTeacherToDoSource {
      return movedLessonTeacherSourceOf(this.item as never) as MovedLessonTeacherToDoSource;
    },
    move(): MovedLessonTeacherMove | null {
      return movedLessonTeacherMove(this.source);
    },
    completed(): boolean {
      return !!this.item.completed;
    },
    /** Rights on the course segment of the original lesson */
    courseRights(): { read: boolean; readTeacher: boolean; updateTeacher: boolean } {
      if (!this.move) {
        return {read: false, readTeacher: false, updateTeacher: false};
      }
      const entity = this.$$getCrudEntity(CrudEntityTypes.COURSE, this.move.course as unknown as Types.ObjectId);
      return {read: entity.canRead(), readTeacher: entity.canRead('teacher'), updateTeacher: entity.canUpdate('teacher')};
    },
    /** Same right as "Lehrkraft klären" in the calendar panel */
    canDecide(): boolean {
      return this.courseRights.readTeacher && this.courseRights.updateTeacher;
    },
    text(): string {
      return movedLessonTeacherText(this.source, this.courseRights.readTeacher);
    },
    calendarLink(): RawLocation | null {
      const access = NavigationDrawerAccess.getEffectiveAccess(this.$store.state.guiAccess || []);
      return this.courseRights.read && access.includes(NavigationDrawerAccess.MY_SCHEDULE) ? movedLessonCalendarLink(this.source) : null;
    },
  },
  methods: {
    /** The course-manager completed the to-do: every to-do list reloads */
    onDecided() {
      this.$emit('decided');
      ToDoBus.$emit('reload:toDos');
    },
  },
});
</script>

<style scoped>
.mlt-todo {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 2px;
}

.mlt-todo--dense {
  gap: 2px;
}

.mlt-todo__text {
  font-size: 13px;
  line-height: 1.4;
}

.mlt-todo--dense .mlt-todo__text {
  font-size: 12px;
}

.mlt-todo__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}

.mlt-todo__actions .cta {
  height: 32px;
  text-decoration: none;
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
